import { LitElement, html, css, TemplateResult, nothing, PropertyValues } from "lit"
import { property } from "lit/decorators"
import { FilterType, VoyageFilterResult, WizardStructureInterface } from "./types"
import { FiSubView, UiStateModel, UiStateModelParam } from "../../../src/models/ui-state-model"
import { AuthorizationRoles, AuthService } from "../../../src/services/auth-service"
import { UserConfigService } from "../../../src/services/user-config-services"
import { VoyMapModel } from "../../../src/models/voy-map-model"
import { FilterUtils } from "./filter-utils"
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons"
import "../../voyage-heavy-weather-alarm-limits/src/breadcrumb-navigation"
import { VoyGlobalFilters } from "./voy-global-filters"
import { SvgImage } from "./empty-filter"
import { FilterEvents } from "../../../src/models/types/voy-fleet-alarm-model.types"

class CustomFilterPopup extends LitElement {
  @property({ type: Boolean, reflect: true }) shown: boolean = false
  @property({ attribute: false }) filterResult?: string
  @property({ attribute: false }) private _services: string[] = []
  @property({ attribute: false }) private _commands: VoyageFilterResult[] = []
  @property({ attribute: false }) private _savedFilters: [] = []
  @property({ type: Array }) private _activeFilters: any[] = []
  @property({ attribute: false }) private _filterText: string = ""
  @property({ attribute: false }) private onchangeFlag = false
  @property({ attribute: false }) private selectedFilterId: string = ""
  @property({ type: Boolean }) public _isUserInternal: boolean = AuthService.userHasRole(AuthorizationRoles.MaerskInternal)
  @property({ attribute: false }) private _selectedFilterName: string = ""
  @property({ attribute: false }) private storedFilterResult: string = ""
  @property({ attribute: false }) private editFilter: boolean = false
  @property({ attribute: false }) private editingFilter: boolean = false
  @property({ attribute: false }) private viewFilter: boolean | undefined = false
  @property({ type: Boolean }) private _displayShareFilterPopup: boolean = false
  @property({ attribute: false }) private _newUsers: string = ""
  @property({ attribute: false }) private _noNewUsers: string = ""
  private shouldReloadFilters = true
  private shouldReloadAfterDelete = true

  constructor() {
    super()
    this._closePopup = this._closePopup.bind(this)
    this.renderCustomFilter = this.renderCustomFilter.bind(this)
    this._clearFilters = this._clearFilters.bind(this)
    this.renderSavedFilter = this.renderSavedFilter.bind(this)
    this._applySelectedFilter = this._applySelectedFilter.bind(this)
    this._viewFilter = this._viewFilter.bind(this)
  }

  static styles = css`
    /* Styles for the popup */
    .sticky-container {
      flex: 1;
      overflow-y: auto;
      padding: 0px 10px;
      margin-top: 11rem;
      width: 60vw;
    }
    .fixed-container {
      width: 60vw;
      position: fixed;
    }
    .container-custom {
      height: 100%;
      display: flex;
      z-index: 100;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
    }
    .container-share-filter {
      height: 80vh;
      display: flex;
      z-index: 1000;
      top: 0;
      left: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      width: 60vw;
      flex: 1;
      padding: 0px 10px;
      margin-top: 11rem;
      position: fixed;
    }

    .create-filter {
      width: 100%;
      padding: 12px 10px;
      box-sizing: border-box;
      border: 1px solid var(--neutrals-grey-06, grey);
      border-radius: 5px;
      font-size: 14px;
    }

    create-filter:focus,
    create-filter:active {
      outline: none;
      box-shadow: none;
    }

    create-filter::placeholder {
      color: var(--neutrals-grey-05, grey);
    }

    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .popup {
      font-family: "Maersk Text";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px;
      background-color: white;
      box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
      border-radius: 5px;
      height: 84%;
      overflow-y: auto;
    }
    .popup-container {
      background-color: white;
    }
    .close-btn {
      margin-top: 10px;
      padding: 10px 15px;
      background-color: #007bff;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .footerButton {
      margin-top: 12px;
      margin-bottom: 12px;
      height: 32px;
      min-width: 80px;
      font-size: 14px;
      font-family: var(--component-paragraph-ff, serif);
      padding: 0px 12px 0px 12px;
      background: none;
      border: none;
      outline: none;
      cursor: pointer;
      border-radius: 3px;
    }

    .footerButton.apply {
      background: rgba(20, 20, 20, 1);
      margin-right: 20px;
      color: var(--neutrals-grey-10, white);
    }

    .footerButton.apply.disabled {
      background: rgba(100, 100, 100, 1);
      cursor: not-allowed;
    }

    .footerButton.save {
      background: rgba(20, 20, 20, 1);
      width: 150px;
      color: var(--neutrals-grey-10, white);
    }

    .footerButton.save.disabled {
      background: rgba(100, 100, 100, 1);
      cursor: not-allowed;
    }

    .footerButton.reset {
      border: 1px solid rgba(20, 20, 20, 1);
      color: rgba(20, 20, 20, 1);
    }

    .cancel {
      color: var(--neutrals-grey-02);
      margin-right: 10px;
      border: 1px solid var(--neutrals-grey-06, grey);
    }

    .filter-actions[disabled] {
      cursor: not-allowed;
    }

    .header {
      font-size: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding-left: 10px;
    }

    .header-content {
      color: var(--neutrals-grey-04, grey);
      padding-left: 10px;
      width: 100%;
    }

    .header-text {
      font-family: MaerskHeadline;
      font-weight: 100;
    }

    .menu-header {
      display: flex;
      margin-top: 1rem;
      margin-bottom: 1rem;
      border-bottom: 2px solid var(--neutrals-grey-05, grey);
    }

    .menu-item {
      cursor: pointer;
      padding: 10px 20px;
      position: relative;
      z-index: 1;
    }

    .menu-item.active {
      color: rgba(20, 20, 20, 1);
    }

    .menu-item.active::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: -2px;
      height: 2px;
      background-color: rgba(20, 20, 20, 1);
      z-index: 2;
    }

    .padding {
      padding-top: 10px;
    }

    .filter-container {
      display: flex;
      align-items: center;
      gap: 15px;
      padding-bottom: 10px;
    }
    .filter-container-main {
      display: flex;
      align-items: center;
      gap: 15px;
      padding-bottom: 10px;
    }

    .input-icon-container {
      position: relative;
      display: flex;
      align-items: center;
      flex-grow: 1;
    }

    .input-icon-container svg {
      position: absolute;
      left: 10px;
      pointer-events: none;
    }

    .filter-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid var(--neutrals-grey-05, grey);
      width: 100%;
    }

    .filter-row input[type="radio"] {
      margin-right: 10px;
    }

    .filter-row span {
      flex: 1; /* Take up the remaining space */
      text-align: left;
    }

    .filter-actions {
      display: flex;
      gap: 0.5rem;
      color: rgba(20, 20, 20, 1);
    }

    .filter-actions button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 5px;
      border: 1px solid rgba(20, 20, 20, 1);
      border-radius: 4px;
    }

    .no-saved-filter,
    .saved-filter-content {
      font-family: "Maersk Text";
      width: 100%;
      height: 600px;
      text-align: center;
      color: var(--neutrals-grey-04, grey);
    }

    .breadcrumb-container {
      display: inline-block;
      padding: 7px;
      border: 1px solid #ddd;
      border-radius: 4px;
      width: auto;
      margin-bottom: 18px;
    }

    .not-found {
      padding-top: 80px;
      padding-bottom: 10px;
    }
    .global-sticky {
      flex: 1;
      overflow-y: auto;
      padding: 10px;
      max-height: 100%;
      width: 60vw;
    }
    .container {
      display: flex;
      flex-direction: column;
      place-content: center;
      width: 695px;
      padding: 30px;
      padding-bottom: 8vh;
      font-family: "Maersk Text";
      font-size: 14px;
    }
    .inputContainer {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      height: 52px;
      width: inherit;
      border-radius: 3px;
      font-size: 16px;
    }
    .error-label {
      font-size: 12px;
      margin-top: 5px;
      display: none;
      color: var(--feedback-danger, red);
    }
    #newUsersInput[show] {
      display: block;
    }
  `

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has("shown")) {
      if (this.shown) {
        this.populateFilterItems()
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener("stored-filter-result-changed", this._handleStoredFilterResultChange)
  }

  private _handleStoredFilterResultChange(event: Event) {
    const customEvent = event as CustomEvent
    this.storedFilterResult = customEvent.detail.storedFilterResult
  }

  private _currentView: string = "savedFilter" // Default active tab
  private _activeTab: string = "savedFilter"

  render() {
    const numberOfActiveFilters = this._commands.reduce((prev, curr) => {
      if (curr!.vals!.includes("maersk")) {
        return prev + curr!.vals!.length - 1
      } else if (curr!.cmd === "tml") {
        return prev
      }
      return prev + curr!.vals!.length
    }, 0)
    VoyGlobalFilters.isFilterApplied = numberOfActiveFilters > 0
    return this.popUpRender()
  }

  private popUpRender() {
    return html`
      <div class=${this.shown ? "container-custom" : ""}>
        <mc-modal ?open=${this.shown} height="80vh" width="65vw" @closing=${this._closePopup}>
          <div class="fixed-container">${this.renderFixedPart()}</div>
          <div class="sticky-container">${this.renderStickyPart()}</div>
        </mc-modal>
        <voyage-popup
          height="80vh"
          width="65vw"
          header-text="Share filter : ${this._selectedFilterName}"
          accept-button-text="Share"
          accept-button-bg="#00233d"
          @accept=${() => this._shareFilter()}
          ?shown=${this._displayShareFilterPopup}
          @close=${() => this._closeVoyagePopup()}>
          <div class="container">
            <div>
              <label for="newUsersInput">Please enter the email id(s) of the user(s) you want to share the filter with.</label>
            </div>
            <div class="inputContainer">
              <input
                class="inputContainer"
                id="newUsersInput"
                type="text"
                label="Maersk Email Id"
                placeholder="Multiple email ids are auto separated by ';'"
                name="newusers"
                .value=${this._newUsers}
                @input=${(evt: InputEvent) => {
                  const input = evt.target as HTMLInputElement
                  let value = input.value

                  // Only autocomplete if the inputType is not a delete
                  if ((evt as any).inputType !== "deleteContentBackward" && (evt as any).inputType !== "deleteContentForward") {
                    if (value.endsWith("@")) {
                      value = value + "maersk.com;"
                      input.value = value // update input field immediately
                    } else if (value.endsWith(".com")) {
                      value = value + ";"
                      input.value = value
                    }
                  }
                  this._newUsers = value
                }} />
            </div>
            <span class="error-label" id="newUsersInput" ?show=${this._noNewUsers.length}>${this._noNewUsers} </span>
          </div>
        </voyage-popup>
      </div>
    `
  }

  private renderFixedPart() {
    return html` <div class="header">
        <h1 class="header-text">Custom Filter</h1>
        <div>
          <button class="footerButton cancel" @click=${this._closePopup}>Cancel</button>
          <button class="footerButton apply ${!this.selectedFilterId && !this.editingFilter ? "disabled" : " "}" @click=${this._applySelectedFilter}>Apply Filter</button>
        </div>
      </div>
      <div class="header-content">
        <div>You can create your customised filter in the Create Custom Filter below, or edit and delete your existing filter from the Saved Filter section.</div>

        <div class="menu-header">
          <div class="menu-item ${this._activeTab === "savedFilter" ? "active" : ""}" @click=${() => this._setActiveTab("savedFilter")}>Saved Filter</div>
          <div class="menu-item ${this._activeTab === "createFilter" ? "active" : ""}" @click=${() => this._setActiveTab("createFilter")}>Create Custom Filter</div>
        </div>
      </div>`
  }

  private renderStickyPart() {
    return html` ${this._currentView === "savedFilter"
      ? html`${this.shouldReloadAfterDelete ? this.renderSavedFilter() : nothing}`
      : this._currentView === "viewFilter"
      ? html`${this.renderViewFilter()}`
      : html`${this.renderCustomFilter()}`}`
  }

  private renderSavedFilter() {
    return html`
      ${this._savedFilters.length > 0
        ? html`
            <div class="saved-filter-content">
              ${this._savedFilters.map(
                (filter: any) => html`
                  <div class="filter-row">
                    <mc-radio
                      name="savedFilter"
                      label=${filter.filterName}
                      ?checked=${this.selectedFilterId === filter.id}
                      value="${filter.id}"
                      @change=${(e: InputEvent) => this.onChangeFilter(e, filter.id, filter.filterName)}></mc-radio>
                    <div class="filter-actions">
                      <button @click="${() => this._deleteFilter(this.selectedFilterId)}" ?disabled="${this.selectedFilterId !== filter.id}" title="Delete">
                        ${SvgIcons.trashCan}
                      </button>
                      <button
                        @click="${() => this._viewFilter(filter.filterName === this._selectedFilterName ? filter.filterName : "", filter.id)}"
                        ?disabled="${this.selectedFilterId !== filter.id}"
                        title="View">
                        ${SvgIcons.fileEye}
                      </button>
                      <button @click="${() => this._editFilter(this.selectedFilterId)}" ?disabled="${this.selectedFilterId !== filter.id}" title="Edit">
                        ${SvgIcons.pencilCustom}
                      </button>
                      <button @click="${() => this.renderShareFilterPopup()}" ?disabled="${this.selectedFilterId !== filter.id}" title="Share">${SvgIcons.shareCustom}</button>
                    </div>
                  </div>
                `
              )}
            </div>
          `
        : html`
            <div class="no-saved-filter">
              <div class="not-found">${SvgImage.EmptyFilterImage}</div>
              We couldn't find any custom filter here, please create a new one.
            </div>
          `}
    `
  }

  private renderCustomFilter() {
    return html`
      <div class="filter-container-main">
        <div class="input-icon-container">
          <input
            type="text"
            class="create-filter"
            ?error=${this.onchangeFlag && !this._filterText}
            placeholder="Type here"
            @input=${this.onChangeFilterText}
            .value=${this._filterText} />
        </div>
        <button
          class="footerButton save ${!(this._filterText && this.storedFilterResult) ? "disabled" : " "}"
          @click=${this._saveFilters}
          ?disabled=${!(this._filterText && this.storedFilterResult)}>
          Save Filter
        </button>
        <button class="footerButton reset" @click=${this._clearFilters}>Reset</button>
      </div>
      <div class="global-sticky">
        <!-- Sticky Start -->
        <div>Select from the below list to create a custom filter:</div>
        ${this.shouldReloadFilters ? html`<voy-global-filters .showCustomFilterButton=${false} .showImoFilter=${true} .createFilter=${true}></voy-global-filters>` : nothing}
      </div>
      <!-- Sticky End -->
    `
  }

  private renderViewFilter() {
    const paths = [
      { name: "Saved Filter", action: () => this._setActiveTab("savedFilter") },
      { name: this._selectedFilterName, action: () => {} },
    ]
    return html`
      ${this.editFilter
        ? html`
            <div class="filter-container">
              <div class="input-icon-container">
                <input
                  type="text"
                  class="create-filter"
                  ?error=${this.onchangeFlag && !this._filterText}
                  placeholder="Type here"
                  @input=${this.onChangeFilterText}
                  .value=${this._selectedFilterName}
                  ?disabled=${!this.editingFilter} />
              </div>
              ${this.editingFilter
                ? html` <button class="footerButton save" @click=${this._editFilters}>Save Filter</button>
                    <button class="footerButton reset" @click=${this._clearFilters}>Reset</button>`
                : html` <button class="footerButton save" @click=${this.onEditFilter}>Edit Filter</button> `}
            </div>
          `
        : ``}
      <div>
        <div class="breadcrumb-container">
          <breadcrumb-navigation .paths=${paths}></breadcrumb-navigation>
        </div>
        ${this.shouldReloadFilters
          ? html`<voy-global-filters
              .showCustomFilterButton=${false}
              .showImoFilter=${true}
              @filters-changed=${this._updateNumberOfActiveFilters}
              .viewFilter=${this.viewFilter}
              .editFilter=${this.editingFilter}></voy-global-filters>`
          : nothing}
      </div>
    `
  }

  firstUpdated() {
    this.populateFilterItems()
    this._loadFilterState()
    this._updateNumberOfActiveFilters()
  }

  private onChangeFilterText(event: { target: { value: string } }) {
    this.onchangeFlag = true
    this._filterText = event!.target!.value
  }

  private _applySelectedFilter() {
    if (this.selectedFilterId) {
      let selectedFilter: any = this._savedFilters.find((item: any) => item.id === this.selectedFilterId)
      if (selectedFilter) {
        let filter = JSON.parse(selectedFilter.data)
        //removing the imo's selected from url
        let imoNumbers = filter?.filters?.match(/ig:([\d+]+)/)
        imoNumbers ? localStorage.setItem("imoGroup", imoNumbers[1]) : null

        let updatedFilters = filter.filters.replace(/ig:[\d+]+/, "ig")
        UiStateModel!.setValue("q", updatedFilters)
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.ListView)
        VoyMapModel?.setZoomLevel(this.getZoomPointFromURL() || 3)
        this._loadFilterState()
        this._updateNumberOfActiveFilters()
        this._closePopup() // Close the modal
        this.requestUpdate()

        localStorage.setItem("appliedFilter", selectedFilter.filterName)
      }
    }
  }
  private getZoomPointFromURL() {
    const searchParams = new URLSearchParams(window.location.search)
    const zoomLevel = searchParams?.get("at")?.split(",")
    if (zoomLevel && zoomLevel[2]) {
      return parseFloat(zoomLevel[2].replace("z", ""))
    }
  }

  public _clearFilters() {
    this._commands = []
    this.storedFilterResult = ""
    UiStateModel!.setValue("q", "")
    this._services = []
    this._loadFilterState()
    this._filterText = ""
    localStorage.setItem("appliedFilter", "")
    this._updateNumberOfActiveFilters()

    // Toggle the flag to force re-rendering of <voy-global-filters>
    this.shouldReloadFilters = false
    this.requestUpdate() // Force the component to update
    setTimeout(() => {
      this.shouldReloadFilters = true
      this.requestUpdate() // Re-render after a short delay
    }, 0)
  }

  private async _deleteFilter(filterId: string) {
    // Find the filter to be deleted
    let savedFilter: any = this._savedFilters.find((filter: any) => filter.id === filterId)

    if (savedFilter) {
      let user = AuthService.getCurrentUserId()
      let payload = {
        id: savedFilter.id,
        data: savedFilter.data,
        filterName: savedFilter.filterName,
        isActive: false,
        isInternalUser: this._isUserInternal,
        userId: user,
      }

      try {
        // Upsert the updated payload
        const deletedFilterName = savedFilter.filterName
        await UserConfigService.upsertFilter(payload)
        this.selectedFilterId = ""
        this.populateFilterItems()
        this.dispatchEvent(
          new CustomEvent(FilterEvents.FilterSaved, {
            detail: {
              message: `${deletedFilterName} has been deleted.`,
              type: "success",
            },
            bubbles: true,
            composed: true,
          })
        )
        this.shouldReloadAfterDelete = false
        this.requestUpdate() // Force the component to update
        setTimeout(() => {
          this.shouldReloadAfterDelete = true
          this.requestUpdate()
        }, 0)
      } catch (err) {
        console.error(err)
      } finally {
        this.requestUpdate()
      }
    } else {
      this.dispatchEvent(
        new CustomEvent(FilterEvents.FilterSaved, {
          detail: {
            message: "Filter not found",
            type: "error",
          },
          bubbles: true,
          composed: true,
        })
      )
      return
    }
  }

  private _viewFilter(filterName: string, filterId: string) {
    this._currentView = "viewFilter"
    this._selectedFilterName = filterName
    const selectedViewFilter = this._activeFilters?.find((filter) => filter.id === filterId)
    const dataparse = JSON.parse(selectedViewFilter.data)
    localStorage.setItem("selectedFilter", dataparse.filters)
    this.viewFilter = true
    this.requestUpdate()
  }

  private _editFilter(filterId: string) {
    this._currentView = "viewFilter"
    const selectedEditFilter: any = this._savedFilters?.find((filter: any) => filter.id === filterId)
    this._selectedFilterName = selectedEditFilter.filterName
    const dataparse = JSON.parse(selectedEditFilter.data)
    localStorage.setItem("selectedFilter", dataparse.filters)
    this.editFilter = true
    this.viewFilter = true
    this.requestUpdate()
  }

  private async _shareFilter() {
    const selectedEditFilter: any = this._savedFilters?.find((filter: any) => filter.id === this.selectedFilterId)
    let users = this._newUsers.split(";").map((user: string) => user.trim())
    if (users.length === 0 || users[0] === "") {
      this._noNewUsers = "Please enter at least one email id to share the filter."
      return
    }
    //loop through the users and check if they are valid maersk email ids
    for (let user of users) {
      if (user === "") continue
      if (!user.endsWith("@maersk.com")) {
        this._noNewUsers = "Please enter valid maersk email ids."
        return
      }
    }

    // Prepare the payload for sharing the filter for each user
    for (let user of users) {
      if (user === "") continue // Skip empty strings
      let payload = {
        id: 0,
        data: selectedEditFilter.data,
        filterName: selectedEditFilter.filterName,
        isActive: true,
        isInternalUser: this._isUserInternal,
        userId: user,
      }
      await UserConfigService.upsertFilter(payload)
        .then(() => {
          this.dispatchEvent(
            new CustomEvent(FilterEvents.FilterSaved, {
              detail: {
                message: `Filter has been shared successfully.`,
                type: "success",
              },
              bubbles: true,
              composed: true,
            })
          )
        })
        .catch((err) => {
          console.error(err)
          this.dispatchEvent(
            new CustomEvent(FilterEvents.FilterSaved, {
              detail: {
                message: `Failed to share filter with ${user}.`,
                type: "error",
              },
              bubbles: true,
              composed: true,
            })
          )
        })
    }

    this._displayShareFilterPopup = false
  }

  private renderShareFilterPopup() {
    this._displayShareFilterPopup = true
    this._newUsers = ""
    this._noNewUsers = ""
  }

  private onChangeFilter(event: any, filterId: string, filterName: string) {
    if (event) {
      this.selectedFilterId = filterId
      this._selectedFilterName = filterName
    }
  }

  private onEditFilter() {
    this.viewFilter = false
    this.editingFilter = true
  }

  private _loadFilterState() {
    const filterString = UiStateModel!.getValue("q")

    if (!filterString?.length) {
      return
    }
    const filter = FilterUtils.stringToFilter(filterString)

    if (!filter?.commands.length) {
      return
    }
    // set services (pills)
    for (const command of filter?.commands) {
      if (command.cmd == FilterType.Service && command.vals?.length) {
        this._services = command.vals
      }
    }
    // set commands (count of enabled filters)
    this._commands = filter?.commands
  }

  private _updateNumberOfActiveFilters() {
    const numberOfActiveFilters = this._commands.reduce((prev, curr) => {
      if (curr!.vals!.includes("maersk")) {
        return prev + curr!.vals!.length - 1
      } else if (curr!.cmd === "tml") {
        return prev
      }
      return prev + curr!.vals!.length
    }, 0)
    this.dispatchEvent(
      new CustomEvent("filters-changed", {
        detail: {
          numberOfActiveFilters,
        },
      })
    )
  }

  private async _saveFilters() {
    if (this._filterText && this.storedFilterResult) {
      let user = AuthService.getCurrentUserId()
      let payload = this.constructPayload(user, this.storedFilterResult)
      let filterNameExists: any = this._savedFilters.find((item: any) => item.filterName === this._filterText)

      if (filterNameExists) {
        this.dispatchEvent(
          new CustomEvent(FilterEvents.FilterSaved, {
            detail: {
              message: `Filter name already exists. Please choose a different name.`,
              type: "error",
            },
            bubbles: true,
            composed: true,
          })
        )
        return
      }

      try {
        await UserConfigService.upsertFilter(payload)
        this.populateFilterItems()
        this.dispatchEvent(
          new CustomEvent(FilterEvents.FilterSaved, {
            detail: {
              message: `${this._filterText} has been created.`,
              type: "success",
            },
            bubbles: true,
            composed: true,
          })
        )
        this._setActiveTab("savedFilter")
        this.storedFilterResult = ""
      } catch (err) {
        console.error(err)
      } finally {
        this.requestUpdate()
      }
    }
  }

  private async _editFilters() {
    // Find the filter to be edited
    let savedFilter: any = this._savedFilters.find((filter: any) => filter.filterName === this._selectedFilterName)
    if (this._selectedFilterName && savedFilter) {
      const name = this._filterText ? this._filterText : this._selectedFilterName
      let user = AuthService.getCurrentUserId()
      let existingFilters = JSON.parse(savedFilter.data).filters
      let payload = {
        id: savedFilter.id,
        data: savedFilter.data,
        filterName: name,
        isActive: true,
        isInternalUser: this._isUserInternal,
        userId: user,
      }

      if (this.storedFilterResult) {
        existingFilters = JSON.parse(savedFilter.data).filters.split(" ")
        payload = {
          id: savedFilter.id,
          data: JSON.stringify({ filters: this.storedFilterResult }),
          filterName: name,
          isActive: true,
          isInternalUser: this._isUserInternal,
          userId: user,
        }
      }

      try {
        await UserConfigService.upsertFilter(payload)
        this.populateFilterItems()
        this.dispatchEvent(
          new CustomEvent(FilterEvents.FilterSaved, {
            detail: {
              message: `${name} has been edited.`,
              type: "success",
            },
            bubbles: true,
            composed: true,
          })
        )
        this._setActiveTab("savedFilter")
        this.storedFilterResult = ""
      } catch (err) {
        console.error(err)
      }
    }
  }

  private _setActiveTab(tab: string) {
    this._currentView = tab
    this._activeTab = tab
    if (tab === "createFilter") {
      this._filterText = ""
      this.selectedFilterId = ""
      this.viewFilter = false
      this.editFilter = false
    }
    if (tab === "savedFilter") {
      this._filterText = ""
      this.selectedFilterId = ""
      this.viewFilter = false
      this.editFilter = false
      this.editingFilter = false
    }

    this.requestUpdate()
  }

  private async populateFilterItems() {
    let user = AuthService.getCurrentUserId()
    try {
      let response = await UserConfigService.getSavedFilter(user)
      if (response) {
        this._savedFilters = response
        this._activeFilters = response
      } else this._savedFilters = []
    } catch (e) {
      console.error(e)
    } finally {
      this.requestUpdate()
    }
  }

  constructPayload(user: any, filters: string) {
    let payload = {
      id: 0,
      data: JSON.stringify({ filters: filters }),
      filterName: this._filterText,
      isActive: true,
      isInternalUser: this._isUserInternal,
      userId: user,
    }
    return payload
  }

  _closePopup() {
    this.dispatchEvent(new Event("close"))
    this.selectedFilterId = ""
    this.viewFilter = false
    this.editFilter = false
    this.editingFilter = false
  }

  _closeVoyagePopup() {
    this._displayShareFilterPopup = false
  }
}

customElements.define("custom-filter-popup", CustomFilterPopup)
